import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { useOFEFetch } from './useOFE';

const today = new Date().toISOString().slice(0, 10);

function DateCell({ planned, actual }) {
  if (!planned) return <span className="text-gray-500">—</span>;
  const isPast = planned < today;
  const isMissed = isPast && !actual;
  return (
    <div>
      <div className={isMissed ? 'text-red-400' : actual ? 'text-green-400' : 'text-gray-300'}>{planned}</div>
      {actual && <div className="text-green-400 text-xs">✓ {actual}</div>}
    </div>
  );
}

export default function OFEScheduleTracker() {
  const { id: project } = useParams();
  const { user } = useAuth();
  const { data: items, loading } = useOFEFetch(
    `/ofe/production?project=${project}&limit=200`, user, [project]
  );

  const late = (items || []).filter(i => i.mfg_ship_date_plan < today && !i.mfg_ship_date_actual);
  const onTime = (items || []).filter(i => i.mfg_ship_date_actual);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-4">
        <Link to={`/ofe/projects/${project}`} className="text-gray-400 hover:text-white text-sm">← {project}</Link>
        <h1 className="text-xl font-bold text-white">Schedule Tracker — <span className="text-blue-400 font-mono">{project}</span></h1>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-800 border border-gray-700 rounded p-3">
          <div className="text-2xl font-bold text-white">{items?.length || 0}</div>
          <div className="text-gray-400 text-sm">Total Items</div>
        </div>
        <div className="bg-red-900/20 border border-red-800 rounded p-3">
          <div className="text-2xl font-bold text-red-400">{late.length}</div>
          <div className="text-gray-400 text-sm">Schedule Gaps</div>
        </div>
        <div className="bg-green-900/20 border border-green-800 rounded p-3">
          <div className="text-2xl font-bold text-green-400">{onTime.length}</div>
          <div className="text-gray-400 text-sm">Shipped</div>
        </div>
      </div>

      {late.length > 0 && (
        <div className="bg-red-900/10 border border-red-800/40 rounded-lg p-3 text-red-300 text-sm">
          ⚠ {late.length} item(s) past planned ship date with no actual ship date recorded.
        </div>
      )}

      {loading && <div className="text-gray-400">Loading...</div>}

      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-gray-400">
            <tr>
              <th className="text-left px-4 py-3">Equipment Type</th>
              <th className="text-left px-4 py-3">Serial #</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Mfg Start</th>
              <th className="text-left px-4 py-3">Ship Plan / Actual</th>
              <th className="text-left px-4 py-3">Delivery Plan / Actual</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {(items || []).map(i => (
              <tr key={i.id} className={`hover:bg-gray-750 ${i.mfg_ship_date_plan < today && !i.mfg_ship_date_actual ? 'bg-red-950/20' : ''}`}>
                <td className="px-4 py-2 text-white">{i.equipment_type}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-300">{i.serial_number || '—'}</td>
                <td className="px-4 py-2 text-xs text-gray-300">{i.production_status?.replace(/_/g, ' ')}</td>
                <td className="px-4 py-2 text-xs text-gray-300">{i.mfg_start_date || '—'}</td>
                <td className="px-4 py-2 text-xs"><DateCell planned={i.mfg_ship_date_plan} actual={i.mfg_ship_date_actual} /></td>
                <td className="px-4 py-2 text-xs"><DateCell planned={i.delivery_date_plan} actual={i.delivery_date_actual} /></td>
              </tr>
            ))}
            {!items?.length && !loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No items</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
